import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, errText } from "@/lib/api";
import { TimeFields, useTimeForm, MODE_LABEL } from "./TimeFields";
import { InterviewCard, useCancel } from "./InterviewBox";

const SlotForm = ({ onAdded }) => {
  const [v, set] = useTimeForm();
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await http.post("/admin/slots", v); toast.success(`Slot added: ${r.data?.when_label ?? ""}`); onAdded(); }
    catch (x) { toast.error(errText(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={add} className="space-y-3" data-testid="slot-form">
      <TimeFields v={v} set={set} prefix="slot" compact />
      <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="slot-add-btn"><Plus className="w-4 h-4" /> Add open slot</button>
    </form>
  );
};

const SlotList = ({ slots, onDelete }) => {
  if (!slots.length) return <p className="text-sm text-[#465F57]" data-testid="slots-empty">No open slots yet. Add a few so applicants can book from their email link.</p>;
  return (
    <ul className="divide-y divide-[#154F49]/10 border border-[#154F49]/15 rounded-2xl bg-white" data-testid="slot-list">
      {slots.map((s) => (
        <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm" data-testid={`slot-${s.id}`}>
          <span><span className="block font-semibold text-[#154F49]">{s.when_label}</span><span className="text-xs text-[#465F57]">{MODE_LABEL[s.mode]} · {s.duration_min} min</span></span>
          {s.booked_application_id
            ? <span className="px-2.5 py-1 rounded-full bg-[#CDA34F]/25 text-[#154F49] text-xs font-bold">Booked</span>
            : <button type="button" onClick={() => onDelete(s)} aria-label={`Delete slot ${s.when_label}`} className="p-2 rounded-lg text-red-800 hover:bg-red-50" data-testid={`slot-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>}
        </li>
      ))}
    </ul>
  );
};

export const InterviewsPanel = () => {
  const [ivs, setIvs] = useState([]);
  const [slots, setSlots] = useState([]);
  const load = useCallback(() => {
    http.get("/admin/interviews").then((r) => setIvs(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errText(e)));
    http.get("/admin/slots").then((r) => setSlots(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  useEffect(() => { load(); return undefined; }, [load]);
  const cancel = useCancel(load);
  const del = async (s) => {
    try { await http.delete(`/admin/slots/${s.id}`); toast.success("Slot removed."); load(); } catch (x) { toast.error(errText(x)); }
  };
  const upcoming = ivs.filter((i) => i.status === "scheduled");
  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="interviews-panel">
      <section className="lg:col-span-3 space-y-3">
        <h2 className="font-serif text-2xl text-[#154F49]">Upcoming interviews <span className="text-[#CDA34F]" data-testid="upcoming-count">{upcoming.length}</span></h2>
        {upcoming.length === 0 && <p className="p-8 text-center text-[#465F57] bg-white rounded-3xl border border-[#154F49]/15">No interviews scheduled. Open an application to set a time or send a booking link.</p>}
        {upcoming.map((iv) => (
          <div key={iv.id} className="space-y-1">
            <p className="text-xs font-bold uppercase tracking-wider text-[#9B742A]">{iv.applicant_name} · {iv.email}</p>
            <InterviewCard iv={iv} onCancel={cancel} />
          </div>
        ))}
      </section>
      <section className="lg:col-span-2 space-y-4 p-5 bg-white rounded-3xl border border-[#154F49]/15 h-fit">
        <div>
          <h2 className="font-serif text-2xl text-[#154F49]">Open slots</h2>
          <p className="text-xs text-[#465F57]">Times applicants can choose from when you send them a booking link. All times Eastern.</p>
        </div>
        <SlotForm onAdded={load} />
        <SlotList slots={slots} onDelete={del} />
      </section>
    </div>
  );
};
